import React from 'react';

import { Badge, Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { Download } from 'lucide-react';

import { LiabilityBadgeCell } from '../../antibody-analysis/components/InnerSequenceTable/LiabilityBadgeCell';
import { NodeCell } from '../../antibody-analysis/components/InnerSequenceTable/NodeCell';
import type { ExportRow } from '../types';

const s = {
  tableWrap: 'border border-solid border-[#e1e5e9] rounded-sm max-h-[420px] overflow-y-auto',
  table: 'w-full border-collapse text-xs',
  th: 'sticky top-0 z-10 bg-white text-left text-[13px] font-semibold text-black px-4 py-2.5 border-0 border-b border-solid border-[#e1e5e9]',
  td: 'px-4 py-2 text-xs align-middle border-0 border-b border-solid border-[#f0f3f1]',
  downloadIcon: 'w-4 h-4',
} as const;

const FORMAT_OPTIONS = [{ value: 'csv', label: 'CSV' }];

interface FinalExportDialogProps {
  opened: boolean;
  onClose: () => void;
  /** The unique sequences that will be written to the file. */
  rows: ExportRow[];
  onExport: () => void;
}
export function FinalExportDialog({ opened, onClose, rows, onExport }: FinalExportDialogProps) {
  const [format, setFormat] = React.useState<string>('csv');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Final Sequence Export"
      centered
      size="lg"
      data-testid="final-export-dialog"
      closeOnClickOutside={false}
      styles={{ title: { flex: 1, textAlign: 'center', fontWeight: 600 } }}
    >
      <Stack gap="lg">
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>Node</th>
                <th className={s.th}>Liabilities</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.nodeId}>
                  <td className={s.td}>
                    <Group gap="sm" wrap="nowrap">
                      <NodeCell nodeLabel={row.nodeLabel} barcodes={row.sequenceData.metadata.barcodes} searchQuery="" />
                      {/* 'Selected' is redundant here — later on this will show the editable chips */}
                      {row.labels
                        .filter((label) => label !== 'Selected')
                        .map((label) => (
                          <Badge key={label} variant="outline" color="gray" radius="sm" size="sm" tt="none" fw={400}>
                            {label}
                          </Badge>
                        ))}
                    </Group>
                  </td>
                  <td className={s.td}>
                    <LiabilityBadgeCell sequenceData={row.sequenceData} searchQuery="" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Group gap="xs" justify="flex-end">
          <Text size="sm">Export as:</Text>
          <Select
            size="xs"
            w={110}
            data={FORMAT_OPTIONS}
            value={format}
            onChange={(value) => setFormat(value ?? 'csv')}
            allowDeselect={false}
            aria-label="Export format"
          />
        </Group>

        <Group gap="lg" justify="flex-end">
          <Button radius="xl" color="#1263c4" disabled={rows.length === 0} leftSection={<Download className={s.downloadIcon} />} onClick={onExport}>
            Download
          </Button>
          <Button radius="xl" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
